/* Subscription gifts in the cart drawer.
 *
 * Capture-phase listener: when the subscribe CTA carries gift IDs, atomically
 * add the gifts and convert the parent line to a subscription, then refresh the
 * drawer. Uses the same `_gift` / `_parent_variant` properties as the PDP flow,
 * so w-cart-item-subscription.js#removeLinkedGifts cleans them up on
 * un-subscribe.
 */

/* ------------------------------------------------------------------
 * Defensive gift → parent qty sync.
 *
 * w-sidecart.js#fetchUpdateCart already tries to propagate the parent
 * quantity to its linked subscription gifts whenever +/- is clicked,
 * but in practice the sync sometimes does not survive the cart refresh
 * (race with section render, discount engine, etc.). This listener runs
 * after every cart drawer refresh, compares each gift line's quantity
 * with its parent's, and if any are out of sync issues a single
 * /cart/update.js call to realign them before re-rendering.
 *
 * Guarded by `syncInFlight` to avoid the infinite loop that would
 * otherwise occur because the sync itself triggers another refresh.
 * ------------------------------------------------------------------ */
(function () {
  let syncInFlight = false;

  function propsOf(item) {
    return Array.isArray(item.properties)
      ? Object.fromEntries(item.properties.map((p) => [p.name, p.value]))
      : item.properties || {};
  }

  async function syncGiftsToParents() {
    if (syncInFlight) return;
    // No gift rows rendered means no gift lines in the cart, so skip the
    // /cart.js round trip. This runs on every page load for every visitor.
    if (!document.querySelector('.cart-item-gift-sub')) return;
    syncInFlight = true;
    try {
      const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());

      // Build map: variantId(string) → parent line quantity.
      // A "parent" here is any non-gift line item.
      const parentQty = {};
      cart.items.forEach((item) => {
        const props = propsOf(item);
        const isSubGift = props._gift === 'true' || props._gift === true;
        const isStepGift = props['_is-gift'] === 'true' || props['_gift-number'];
        if (isSubGift || isStepGift) return;
        parentQty[String(item.variant_id)] = item.quantity;
      });

      // Find gift lines whose qty doesn't match their parent's qty.
      const updates = {};
      cart.items.forEach((item) => {
        const props = propsOf(item);
        const isSubGift = props._gift === 'true' || props._gift === true;
        const isOtherSystem = props['_is-gift'] || props['_gift-number'];
        if (!isSubGift || isOtherSystem) return;
        const target = parentQty[String(props._parent_variant)];
        if (target === undefined) return;
        if (Number(item.quantity) !== Number(target)) {
          updates[item.key] = target;
        }
      });

      if (Object.keys(updates).length === 0) return;

      const sideCart = document.querySelector('side-cart');
      const sections = sideCart && typeof sideCart.getSectionsToRender === 'function'
        ? [...new Set(sideCart.getSectionsToRender().map((s) => s.section))]
        : [];

      const updateResp = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, sections }),
      });
      const updatedCart = await updateResp.json();

      if (sideCart) {
        sideCart.dispatchEvent(
          new CustomEvent('dispatch:cart-drawer:refresh', {
            detail: { cart: updatedCart },
          })
        );
      }
    } catch (err) {
      console.error('[Gift Sync] defensive sync failed:', err);
    } finally {
      // Release the guard slightly after the refresh dispatch settles so
      // we don't immediately retrigger on the cascading refresh event.
      setTimeout(() => {
        syncInFlight = false;
      }, 300);
    }
  }

  function bindSync() {
    const sideCart = document.querySelector('side-cart');
    if (!sideCart) return;
    sideCart.addEventListener('dispatch:cart-drawer:refresh', () => {
      // Defer to let the drawer re-render first, then check / fix.
      setTimeout(syncGiftsToParents, 50);
    });
    syncGiftsToParents();
  }

  document.addEventListener('DOMContentLoaded', bindSync);
})();
/* ------------------------------------------------------------------
 * Compact gift sub-row "remove" click.
 *
 * cart-items.js is not loaded in the side-cart drawer (0-cart-drawer.liquid
 * references a non-existent `w-cart-items.js`), so removal is handled here:
 * a delegated listener that removes the line by `data-item-key` and triggers
 * the standard drawer refresh.
 *
 * This is the only handler for these rows — cart-item-gift-sub.liquid
 * deliberately omits `js-remove-item` so cart-items.js doesn't also fire on
 * /cart and remove by a line index that this call is about to invalidate.
 * ------------------------------------------------------------------ */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.cart-item-gift-sub__remove');
  if (!btn) return;
  e.preventDefault();

  const row = btn.closest('.cart-item-gift-sub');
  const itemKey = row?.dataset.itemKey;
  if (!itemKey) return;

  // Optimistic dim while the request is in flight.
  row.classList.add('cart-item--loading');

  const sideCart = document.querySelector('side-cart');
  const sections = sideCart && typeof sideCart.getSectionsToRender === 'function'
    ? [...new Set(sideCart.getSectionsToRender().map((s) => s.section))]
    : [];

  try {
    const resp = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [itemKey]: 0 }, sections }),
    });
    const cart = await resp.json();
    if (sideCart) {
      sideCart.dispatchEvent(
        new CustomEvent('dispatch:cart-drawer:refresh', { detail: { cart } })
      );
    }
  } catch (err) {
    console.error('[Gift remove] failed:', err);
    row.classList.remove('cart-item--loading');
  }
});

document.addEventListener(
  'click',
  async (e) => {
    const cta = e.target.closest('.cart-item__subscribe-cta');
    if (!cta) return;

    // Only CTAs whose variant declares a gift bundle take this path; the rest
    // fall through to the plain subscribe handler.
    // RA_PT_PDP_4 | Free Travel Mug — Var A subscribes with the gift set that keeps the mug.
    const giftIds = (window.cFreeTravelMugVarA?.() && cta.dataset.cGiftIdsAbtest) || cta.dataset.cGiftIds;
    if (!giftIds) return;

    if (cta.dataset.cBusy === 'true') return;

    const wrapper = cta.closest('cart-item-subscription');
    const variantId = wrapper?.dataset.variantId;
    const lineIndex = parseInt(wrapper?.dataset.lineIndex, 10);
    const quantity = parseInt(wrapper?.dataset.quantity, 10);
    const planId = cta.dataset.planId;
    if (!variantId || !lineIndex || !planId) return;

    cta.dataset.cBusy = 'true';
    e.preventDefault();
    e.stopImmediatePropagation();

    const cartItem = cta.closest('.cart-item');
    if (cartItem) cartItem.classList.add('cart-item--loading');

    try {
      // 1. Convert the parent line to a subscription FIRST. The "Cadeau offert"
      //    automatic discount that makes the gift free only matches when the
      //    parent line already has a selling_plan at the moment the gift is
      //    added — the PDP flow gets this for free by sending both in a single
      //    /cart/add.js call with selling_plan attached.
      await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: quantity,
          selling_plan: parseInt(planId, 10),
        }),
      });

      // 2. Then add the gifts, so the discount engine sees the subscription
      //    parent on this cart write and applies "Cadeau offert". Gift qty
      //    mirrors the parent line qty at click time.
      const giftQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      const items = giftIds.split(',').map((id) => ({
        id: parseInt(id, 10),
        quantity: giftQty,
        properties: {
          _gift: 'true',
          _parent_variant: String(variantId),
        },
      }));
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
    } catch (err) {
      console.error('[Gift + subscribe] failed:', err);
    } finally {
      const sideCart = document.querySelector('side-cart');
      if (sideCart) {
        sideCart.dispatchEvent(
          new CustomEvent('dispatch:cart-drawer:refresh', { detail: { open: true } })
        );
      }
      delete cta.dataset.cBusy;
    }
  },
  true
);
